/**
 * Les ABSENCES des techniciens, la journée de travail et l'export des heures prévues (lot PL2, ADR-0015 art. 14).
 *
 * Une absence : un technicien, un type (congé, formation, maladie / arrêt, autre), du jour au jour inclus. Elle grise les
 * cases de la grille et retire ses jours ouvrés de la capacité du technicien — jamais un refus d'affecter une visite : la
 * grille avertit, c'est tout. Tout le monde voit le motif (Benjamin, 2026-10-21).
 *
 * Se tromper = retirer l'absence et en déclarer une autre (elle est archivée, jamais supprimée).
 * La journée de travail (7 h par défaut) fixe la capacité d'une semaine : du lundi au vendredi, hors jours d'absence.
 * L'export est un CSV des heures PRÉVUES par technicien et par type (les heures réelles viendront des bons validés).
 *
 * Chaque geste est enregistré tout de suite ; un refus s'affiche en tête du panneau APRÈS la relecture de la liste.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api, type Absence, type GenreAbsence, type TechnicienPlanning } from '../api/client';
import {
  GENRES_ABSENCE,
  LIBELLE_ABSENCE,
  SAISIE_ABSENCE_VIDE,
  adresseCsv,
  csvHeures,
  heuresDeLaJournee,
  lireAbsence,
  minutesDeLaJournee,
  moisDe,
  nomFichierHeures,
  phraseAbsenceCreee,
  phrasePeriode,
  type SaisieAbsence,
} from '../domaine/planning-absences';
import { ErreurFormulaire, Panneau } from './Formulaire';

export function GestionAbsences({
  peutGerer,
  techniciens,
  aujourdhui,
  dayMinutes,
  onChange,
  onFermer,
}: {
  peutGerer: boolean;
  /** Les techniciens ACTIFS de la grille (on ne déclare pas d'absence à un archivé). */
  techniciens: readonly TechnicienPlanning[];
  /** Le jour d'aujourd'hui, celui du serveur (le mois de l'export en part). */
  aujourdhui: string;
  dayMinutes: number;
  /** Relire la grille : une absence change les cases et les capacités. */
  onChange: () => void;
  onFermer: () => void;
}) {
  const [liste, setListe] = useState<readonly Absence[] | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<SaisieAbsence>(SAISIE_ABSENCE_VIDE);
  const [refus, setRefus] = useState<string | null>(null);
  const [journee, setJournee] = useState(heuresDeLaJournee(dayMinutes));
  const [periode, setPeriode] = useState(() => moisDe(aujourdhui));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const l = await api.absencesPlanning(aujourdhui);
      setListe(l.items);
      setErreurLecture(null);
    } catch (e) {
      setErreurLecture(e instanceof ApiError ? e.message : 'Chargement des absences impossible.');
    }
  }, [aujourdhui]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Un geste : relire la liste PUIS annoncer, pour que le rechargement n'efface pas le message. */
  const agir = async (action: () => Promise<unknown>, phrase: string | (() => string)) => {
    setEnCours(true);
    setErreur(null);
    setSucces(null);
    try {
      await action();
      await charger();
      onChange();
      setSucces(typeof phrase === 'function' ? phrase() : phrase);
      return true;
    } catch (e) {
      await charger();
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Action impossible.'));
      return false;
    } finally {
      setEnCours(false);
    }
  };

  const champ = (cle: keyof SaisieAbsence, valeur: string) => {
    setRefus(null);
    setSaisie((s) => ({ ...s, [cle]: valeur }));
  };

  const declarer = async () => {
    const lue = lireAbsence(saisie);
    if (!lue.ok) {
      setRefus(lue.erreur);
      return;
    }
    setRefus(null);
    const nom = techniciens.find((t) => t.id === lue.corps.technicianId)?.name ?? 'ce technicien';
    if (await agir(() => api.creerAbsence(lue.corps), () => phraseAbsenceCreee(nom, lue.corps.kind, lue.corps.from, lue.corps.to))) {
      setSaisie(SAISIE_ABSENCE_VIDE);
    }
  };

  const regler = async () => {
    const minutes = minutesDeLaJournee(journee);
    if (minutes === null) {
      setErreur(new Error('La journée de travail : entre 1 h et 24 h (« 7 », « 7,5 » ou « 7h30 »).'));
      return;
    }
    await agir(() => api.definirCapacitePlanning(minutes), `Journée de travail réglée à ${heuresDeLaJournee(minutes)} h.`);
  };

  const exporter = async () => {
    setEnCours(true);
    setErreur(null);
    setSucces(null);
    try {
      const h = await api.heuresPrevues(periode.from, periode.to);
      // Une adresse `data:` (pas d'adresse temporaire du navigateur) : le fichier se télécharge, rien d'autre n'est ouvert.
      const lien = window.document.createElement('a');
      lien.href = adresseCsv(csvHeures(h));
      lien.download = nomFichierHeures(h.from, h.to);
      window.document.body.appendChild(lien);
      lien.click();
      lien.remove();
      setSucces(`Fichier ${nomFichierHeures(h.from, h.to)} téléchargé : ${h.lignes.length} ${h.lignes.length > 1 ? 'lignes' : 'ligne'}.`);
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Export impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre="Absences et temps de travail"
      sousTitre="Congés, formations, arrêts : la grille grise ces jours-là et la capacité les retire."
      onFermer={onFermer}
      actions={
        <button type="button" className="secondaire" onClick={onFermer}>
          Fermer
        </button>
      }
    >
      {succes && <div className="message succes">{succes}</div>}
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
      {erreurLecture && <div className="message erreur">{erreurLecture}</div>}

      {peutGerer && (
        <div className="champ absences-saisie">
          <strong>Déclarer une absence</strong>
          <label htmlFor="absence-technicien">Technicien</label>
          <select id="absence-technicien" value={saisie.technicianId} disabled={enCours} onChange={(e) => champ('technicianId', e.target.value)}>
            <option value="">Choisir…</option>
            {techniciens.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <label htmlFor="absence-type">Type</label>
          <select id="absence-type" value={saisie.kind} disabled={enCours} onChange={(e) => champ('kind', e.target.value)}>
            <option value="">Choisir…</option>
            {GENRES_ABSENCE.map((g) => (
              <option key={g} value={g}>
                {LIBELLE_ABSENCE[g]}
              </option>
            ))}
          </select>
          <div className="saisie-ligne">
            <div>
              <label htmlFor="absence-du">Premier jour</label>
              <input id="absence-du" type="date" value={saisie.from} disabled={enCours} onChange={(e) => champ('from', e.target.value)} />
            </div>
            <div>
              <label htmlFor="absence-au">Dernier jour (inclus)</label>
              <input id="absence-au" type="date" value={saisie.to} min={saisie.from || undefined} disabled={enCours} onChange={(e) => champ('to', e.target.value)} />
            </div>
          </div>
          <p className="aide">Un seul jour : laissez le dernier jour vide.</p>
          {refus && <p className="aide erreur-champ">{refus}</p>}
          <button type="button" disabled={enCours} onClick={() => void declarer()}>
            Enregistrer l’absence
          </button>
        </div>
      )}

      <div className="champ">
        <strong>Absences à venir</strong>
        {liste === null && !erreurLecture && <div className="vide">Chargement…</div>}
        {liste !== null && liste.length === 0 && <div className="vide">Aucune absence à venir.</div>}
        {liste !== null && liste.length > 0 && (
          <ul className="liste-absences">
            {liste.map((a) => (
              <li key={a.id}>
                <div>
                  <strong>{a.technicianName}</strong> — {LIBELLE_ABSENCE[a.kind as GenreAbsence]}
                  <div className="aide">{phrasePeriode(a.from, a.to)}</div>
                </div>
                {peutGerer && (
                  <button
                    type="button"
                    className="secondaire discret"
                    disabled={enCours}
                    aria-label={`Retirer l’absence de ${a.technicianName} ${phrasePeriode(a.from, a.to)}`}
                    onClick={() => {
                      if (window.confirm(`Retirer cette absence (${a.technicianName}, ${phrasePeriode(a.from, a.to)}) ? Les jours reprennent leur capacité.`)) {
                        void agir(() => api.retirerAbsence(a.id), 'Absence retirée.');
                      }
                    }}
                  >
                    Retirer
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="champ">
        <strong>Journée de travail</strong>
        <p className="aide">Sert à la capacité d’une semaine : du lundi au vendredi, sans les jours d’absence. 7 h par défaut.</p>
        <div className="saisie-ligne">
          <input
            type="text"
            inputMode="decimal"
            aria-label="Heures d’une journée de travail"
            className="absences-journee"
            value={journee}
            disabled={!peutGerer || enCours}
            onChange={(e) => setJournee(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void regler();
            }}
          />
          <span>h par jour</span>
          {peutGerer && (
            <button type="button" className="secondaire" disabled={enCours} onClick={() => void regler()}>
              Régler
            </button>
          )}
        </div>
      </div>

      <div className="champ">
        <strong>Exporter les heures prévues</strong>
        <p className="aide">Un fichier CSV (Excel) : par technicien et par type, les heures PRÉVUES et les jours d’absence. Les heures réelles viendront des bons validés.</p>
        <div className="saisie-ligne">
          <div>
            <label htmlFor="export-du">Du</label>
            <input id="export-du" type="date" value={periode.from} disabled={enCours} onChange={(e) => setPeriode((p) => ({ ...p, from: e.target.value }))} />
          </div>
          <div>
            <label htmlFor="export-au">Au</label>
            <input id="export-au" type="date" value={periode.to} min={periode.from || undefined} disabled={enCours} onChange={(e) => setPeriode((p) => ({ ...p, to: e.target.value }))} />
          </div>
        </div>
        <button type="button" className="secondaire" disabled={enCours || periode.from === '' || periode.to === ''} onClick={() => void exporter()}>
          Télécharger le fichier
        </button>
      </div>
    </Panneau>
  );
}
